import * as XLSX from 'xlsx-js-style'
import { cloneDeep, groupBy } from 'lodash'
import { Dictionary } from '@reduxjs/toolkit'

import { addSalesTax } from '../../../../helpers/payment'
import { convertPrice } from '../../../../helpers/price'

import { IRegistrationFeesInterface } from '../../../../models/registration-fees/registrationFees.interface'

import {
  IPaymentStatus,
  IPaymentType,
  PAYMENT_STATUS,
  PAYMENT_TYPE,
} from '../../../../types/payment'

export interface IFinanceTotal {
  price: number
  paymentStatus: IPaymentStatus
  paymentType: IPaymentType
  isScratched: boolean
  refundAmountAdded: boolean
  amountRefunded: number
  includeSalesTax: boolean
  created: string | Date | null | undefined
}

export interface IPaymentMethodRow {
  account: string
  paid: number
  sold: number
  description: string
  refunded: number
  banked: number
  transactions: number
}

export interface ICategoryRow {
  account: string
  paid: number
  sold: number
  scratched: number
  refunded: number
  banked: number
  pending: number
  forecast: number
  description: string
  transactions: number
}

export interface TransactionRow {
  recipientName: string
  feeName: string
  amount: number
  salesTax: number
  isQualify?: boolean | null
  qualifyFee?: string | null
  paymentType: string | null
  paymentStatus: string
  amountRefunded: number | null
  created: string
  paidDate: string
}

interface IGeneratePaymentMethodsFinanceReport {
  workbook: XLSX.WorkBook
  rows: IPaymentMethodRow[]
  currency: string
  showField: string
}

interface IGenerateCategoriesFinanceReport {
  workbook: XLSX.WorkBook
  rows: ICategoryRow[]
  currency: string
  showField: string
}

interface IGenerateTransactionsFinanceReport {
  workbook: XLSX.WorkBook
  rows: TransactionRow[]
  currency: string
}

const capitalizeFirst = (value?: string | null | boolean): string =>
  value ? String(value).charAt(0).toUpperCase() + String(value).slice(1) : ''

function headerCell(value: string, horizontal: 'center' | 'right' = 'center'): XLSX.CellObject {
  return {
    v: value,
    t: 's',
    s: {
      font: { bold: true, sz: 13 },
      alignment: { horizontal: horizontal, vertical: 'center', wrapText: true },
    },
  }
}

function dataCell(
  value: string | number | boolean | null,
  type: 's' | 'n' = 's',
  extraStyle: Record<string, unknown> = {}
): XLSX.CellObject {
  return {
    v: value ?? '',
    t: value === null || value === undefined ? 's' : type,
    s: {
      alignment: { horizontal: type === 'n' ? 'right' : 'center', vertical: 'center' },
      ...extraStyle,
    },
  }
}

function totalCell(
  value: string | number,
  type: 's' | 'n' = 's',
  extraStyle: Record<string, unknown> = {}
): XLSX.CellObject {
  return {
    v: value,
    t: type,
    s: {
      font: { bold: true },
      alignment: { horizontal: type === 'n' ? 'right' : 'center', vertical: 'center' },
      ...extraStyle,
    },
  }
}

export function generatePaymentMethodsFinanceReport({
  workbook,
  rows,
  currency,
  showField,
}: IGeneratePaymentMethodsFinanceReport): void {
  // ── Build worksheet data ──────────────────────────────────────────────────
  const wsData: XLSX.CellObject[][] = []

  // Row 1 – headers
  wsData.push([
    headerCell('#'),
    headerCell('Description'),
    headerCell(`Sold (${currency})`, 'right'),
    headerCell(`Paid (${currency})`, 'right'),
    headerCell(`Refunded (${currency})`, 'right'),
    headerCell(`Banked (${currency})`, 'right'),
    headerCell('Transactions'),
    headerCell('Show'),
  ])

  // Data rows
  let totalSold = 0
  let totalPaid = 0
  let totalRefunded = 0
  let totalBanked = 0
  let totalTransactions = 0

  rows.forEach((row, idx) => {
    totalSold += row.sold ?? 0
    totalPaid += row.paid ?? 0
    totalRefunded += row.refunded ?? 0
    totalBanked += row.banked ?? 0
    totalTransactions += row.transactions ?? 0

    wsData.push([
      dataCell(idx + 1, 'n', {
        alignment: { horizontal: 'center' },
      }),
      dataCell(row.description),
      dataCell(convertPrice(row.sold) / 100, 's', {
        alignment: { horizontal: 'right' },
      }),
      dataCell(convertPrice(row.paid) / 100, 's', {
        alignment: { horizontal: 'right' },
      }),
      dataCell(convertPrice(row.refunded) / 100, 's', {
        alignment: { horizontal: 'right' },
      }),
      dataCell(convertPrice(row.banked) / 100, 's', {
        alignment: { horizontal: 'right' },
      }),
      dataCell(row.transactions),
      dataCell(showField),
    ])
  })

  // Total row
  wsData.push([
    totalCell(''),
    totalCell('Total'),
    totalCell(convertPrice(totalSold) / 100, 's', {
      alignment: { horizontal: 'right' },
    }),
    totalCell(convertPrice(totalPaid) / 100, 's', {
      alignment: { horizontal: 'right' },
    }),
    totalCell(convertPrice(totalRefunded) / 100, 's', {
      alignment: { horizontal: 'right' },
    }),
    totalCell(convertPrice(totalBanked) / 100, 's', {
      alignment: { horizontal: 'right' },
    }),
    totalCell(totalTransactions),
    totalCell(showField),
  ])

  // ── Create worksheet ──────────────────────────────────────────────────────
  const ws = XLSX.utils.aoa_to_sheet(wsData)

  // Column widths
  ws['!cols'] = [
    { wch: 10 }, // #
    { wch: 30 }, // Description
    { wch: 20 }, // Sold
    { wch: 20 }, // Paid
    { wch: 20 }, // Refunded
    { wch: 20 }, // Banked
    { wch: 20 }, // Transactions
    { wch: 50 }, // Show
  ]

  // Row heights
  ws['!rows'] = [
    { hpt: 24 }, // header
    ...Array(rows.length).fill({ hpt: 18 }), // data rows
    { hpt: 20 }, // total row
  ]

  XLSX.utils.book_append_sheet(workbook, ws, 'Payment Methods')
}

export function generateCategoriesFinanceReport({
  workbook,
  rows,
  currency,
  showField,
}: IGenerateCategoriesFinanceReport): void {
  // ── Build worksheet data ──────────────────────────────────────────────────
  const wsData: XLSX.CellObject[][] = []

  // Row 1 – headers
  wsData.push([
    headerCell('#'),
    headerCell('Description'),
    headerCell(`Sold (${currency})`, 'right'),
    headerCell(`Paid (${currency})`, 'right'),
    headerCell(`Scratched (${currency})`, 'right'),
    headerCell(`Refunded (${currency})`, 'right'),
    headerCell(`Banked (${currency})`, 'right'),
    headerCell(`Pending (${currency})`, 'right'),
    headerCell(`Forecast (${currency})`, 'right'),
    headerCell('Transactions'),
    headerCell('Show'),
  ])

  // Data rows
  let totalSold = 0
  let totalPaid = 0
  let totalScratched = 0
  let totalRefunded = 0
  let totalBanked = 0
  let totalPending = 0
  let totalForecast = 0
  let totalTransactions = 0

  rows.forEach((row, idx) => {
    totalSold += row.sold ?? 0
    totalPaid += row.paid ?? 0
    totalScratched += row.scratched ?? 0
    totalRefunded += row.refunded ?? 0
    totalBanked += row.banked ?? 0
    totalPending += row.pending ?? 0
    totalForecast += row.forecast ?? 0
    totalTransactions += row.transactions ?? 0

    wsData.push([
      dataCell(idx + 1, 'n', {
        alignment: { horizontal: 'center' },
      }),
      dataCell(row.description),
      dataCell(convertPrice(row.sold) / 100, 's', {
        alignment: { horizontal: 'right' },
      }),
      dataCell(convertPrice(row.paid) / 100, 's', {
        alignment: { horizontal: 'right' },
      }),
      dataCell(convertPrice(row.scratched) / 100, 's', {
        alignment: { horizontal: 'right' },
      }),
      dataCell(convertPrice(row.refunded) / 100, 's', {
        alignment: { horizontal: 'right' },
      }),
      dataCell(convertPrice(row.banked) / 100, 's', {
        alignment: { horizontal: 'right' },
      }),
      dataCell(convertPrice(row.pending) / 100, 's', {
        alignment: { horizontal: 'right' },
      }),
      dataCell(convertPrice(row.forecast) / 100, 's', {
        alignment: { horizontal: 'right' },
      }),
      dataCell(row.transactions),
      dataCell(showField),
    ])
  })

  // Total row
  wsData.push([
    totalCell(''),
    totalCell('Total'),
    totalCell(convertPrice(totalSold) / 100, 's', {
      alignment: { horizontal: 'right' },
    }),
    totalCell(convertPrice(totalPaid) / 100, 's', {
      alignment: { horizontal: 'right' },
    }),
    totalCell(convertPrice(totalScratched) / 100, 's', {
      alignment: { horizontal: 'right' },
    }),
    totalCell(convertPrice(totalRefunded) / 100, 's', {
      alignment: { horizontal: 'right' },
    }),
    totalCell(convertPrice(totalBanked) / 100, 's', {
      alignment: { horizontal: 'right' },
    }),
    totalCell(convertPrice(totalPending) / 100, 's', {
      alignment: { horizontal: 'right' },
    }),
    totalCell(convertPrice(totalForecast) / 100, 's', {
      alignment: { horizontal: 'right' },
    }),
    totalCell(totalTransactions),
    totalCell(showField),
  ])

  // ── Create worksheet ──────────────────────────────────────────────────────
  const ws = XLSX.utils.aoa_to_sheet(wsData)

  // Column widths
  ws['!cols'] = [
    { wch: 10 }, // #
    { wch: 50 }, // Description
    { wch: 20 }, // Sold
    { wch: 20 }, // Paid
    { wch: 20 }, // Scratched
    { wch: 20 }, // Refunded
    { wch: 20 }, // Banked
    { wch: 20 }, // Pending
    { wch: 20 }, // Forecast
    { wch: 20 }, // Transactions
    { wch: 50 }, // Show
  ]

  // Row heights
  ws['!rows'] = [
    { hpt: 24 }, // header
    ...Array(rows.length).fill({ hpt: 18 }), // data rows
    { hpt: 20 }, // total row
  ]

  XLSX.utils.book_append_sheet(workbook, ws, 'Categories')
}

export function generateTransactionsFinanceReport({
  workbook,
  rows,
  currency,
}: IGenerateTransactionsFinanceReport): void {
  // ── Build worksheet data ──────────────────────────────────────────────────
  const wsData: XLSX.CellObject[][] = []

  // Row 1 – headers
  wsData.push([
    headerCell('#'),
    headerCell('Recipient Name'),
    headerCell('Fee Name (Order)'),
    headerCell(`Amount (${currency})`, 'right'),
    headerCell('Is Qualify?'),
    headerCell(`Qualify Fee Amount (${currency})`),
    headerCell(`Sales Tax (${currency})`, 'right'),
    headerCell('Payment Method'),
    headerCell('Status'),
    headerCell(`Amount Refunded (${currency})`),
    headerCell('Created'),
    headerCell('Paid Date'),
  ])

  // Data rows
  rows.forEach((row, idx) => {
    wsData.push([
      dataCell(idx + 1, 'n', {
        alignment: { horizontal: 'center' },
      }),
      dataCell(row.recipientName),
      dataCell(row.feeName),
      dataCell(row.amount, 's', {
        alignment: { horizontal: 'right' },
      }),
      dataCell(capitalizeFirst(row.isQualify)),
      dataCell(capitalizeFirst(row.qualifyFee)),
      dataCell(convertPrice(row.salesTax) / 100, 's', {
        alignment: { horizontal: 'right' },
      }),
      dataCell(capitalizeFirst(row.paymentType)),
      dataCell(capitalizeFirst(row.paymentStatus)),
      dataCell(row.amountRefunded),
      dataCell(row.created),
      dataCell(row.paidDate),
    ])
  })

  // ── Create worksheet ──────────────────────────────────────────────────────
  const ws = XLSX.utils.aoa_to_sheet(wsData)

  // Column widths
  ws['!cols'] = [
    { wch: 10 }, // #
    { wch: 25 }, // Recipient Name
    { wch: 40 }, // Fee Name
    { wch: 15 }, // Amount
    { wch: 20 }, // Is Qualify?
    { wch: 30 }, // Qualify Fee Amount
    { wch: 20 }, // Sales Tax
    { wch: 25 }, // Payment Method
    { wch: 20 }, // Status
    { wch: 25 }, // Amount Refunded
    { wch: 30 }, // Created
    { wch: 30 }, // Paid Date
  ]

  // Row heights
  ws['!rows'] = [
    { hpt: 24 }, // header
    ...Array(rows.length).fill({ hpt: 18 }), // data rows
  ]

  XLSX.utils.book_append_sheet(workbook, ws, 'Transactions')
}

export const customRows = (
  custom: Dictionary<IRegistrationFeesInterface[]>,
  prefix: string,
  accountCode: string,
  startChar: number,
  chargeSalesTax: boolean = false
) => {
  return Object.entries(custom).map(([title, items], index) => {
    const {
      paidAmount,
      soldAmount,
      refundedAmount,
      scratchedAmount,
      bankedAmount,
      pendingAmount,
      forecastAmount,
    } = getTotalFinanceData({
      items:
        items?.map((item) => ({
          price: item.feesPrice,
          paymentStatus: item.paymentStatus,
          paymentType: item.paymentType,
          isScratched: item.isScratched || false,
          refundAmountAdded: item.refundAmountAdded || false,
          amountRefunded: item.amountRefunded || 0,
          includeSalesTax: item.includeSalesTax,
          created: item.created,
        })) || [],
      chargeSalesTax: chargeSalesTax,
    })

    return {
      account: `${accountCode}${String.fromCharCode(startChar + index)}`,
      paid: paidAmount,
      sold: soldAmount,
      refunded: refundedAmount,
      scratched: scratchedAmount,
      banked: bankedAmount,
      pending: pendingAmount,
      forecast: forecastAmount,
      description: `${prefix} - ${title}`,
      transactions: 3,
    }
  })
}

export const getFeesCustomFinanceData = ({
  registrationsFees,
  category,
  excludeTitles,
}: {
  registrationsFees: IRegistrationFeesInterface[]
  category: string
  excludeTitles: string[]
}) => {
  return groupBy(
    registrationsFees.filter(
      (item) =>
        item.feesItemCategory === category &&
        item.feesTitle &&
        !excludeTitles.includes(item.feesTitle)
    ),
    'feesTitle'
  )
}

export const getFeesFinanceData = ({
  registrationsFees,
  chargeSalesTax = false,
  category,
  title,
}: {
  registrationsFees: IRegistrationFeesInterface[]
  chargeSalesTax: boolean | undefined
  category: string
  title: string
}) => {
  const itemsByCategoryAndTitle = registrationsFees.filter(
    (item) => item.feesItemCategory === category && item.feesTitle === title
  )
  const {
    paidAmount,
    soldAmount,
    refundedAmount,
    scratchedAmount,
    pendingAmount,
    forecastAmount,
    bankedAmount,
    paidItems,
  } = getTotalFinanceData({
    items: itemsByCategoryAndTitle.map((item) => ({
      price: item.feesPrice,
      paymentStatus: item.paymentStatus,
      paymentType: item.paymentType,
      isScratched: item.isScratched || false,
      refundAmountAdded: item.refundAmountAdded || false,
      amountRefunded: item.amountRefunded || 0,
      includeSalesTax: item.includeSalesTax,
      created: item.created,
    })),
    chargeSalesTax: chargeSalesTax,
  })
  return {
    paidAmount,
    soldAmount,
    refundedAmount,
    scratchedAmount,
    pendingAmount,
    forecastAmount,
    bankedAmount,
    paidItems,
  }
}

export const getTotalFinanceData = ({
  items,
  paymentType,
  chargeSalesTax = false,
}: {
  items: IFinanceTotal[]
  paymentType?: IPaymentType
  chargeSalesTax: boolean | undefined
}) => {
  let soldItems = cloneDeep(items)
  if (paymentType) soldItems = soldItems.filter((item) => item.paymentType === paymentType)

  let soldAmount = 0
  soldItems.forEach((item) => {
    soldAmount +=
      item.price + addSalesTax(item.created, item.price, item.includeSalesTax, chargeSalesTax)
  })

  let paidItems = soldItems.filter((item) => item.paymentStatus === PAYMENT_STATUS.PAID)
  let paidAmount = 0
  paidItems.forEach((item) => {
    paidAmount +=
      item.price + addSalesTax(item.created, item.price, item.includeSalesTax, chargeSalesTax)
  })

  let scratchedAmount = 0
  soldItems.forEach((item) => {
    if (item.isScratched) {
      scratchedAmount +=
        item.price + addSalesTax(item.created, item.price, item.includeSalesTax, chargeSalesTax)
    }
  })

  let refundedAmount = 0
  soldItems.forEach((item) => {
    if (
      item.paymentType === PAYMENT_TYPE.CARD &&
      item.paymentStatus === PAYMENT_STATUS.PAID &&
      item.isScratched
    ) {
      refundedAmount +=
        item.amountRefunded +
        addSalesTax(item.created, item.amountRefunded, item.includeSalesTax, chargeSalesTax)
    } else if (item.paymentStatus === PAYMENT_STATUS.PAID && item.isScratched) {
      refundedAmount +=
        item.price + addSalesTax(item.created, item.price, item.includeSalesTax, chargeSalesTax)
    }
  })

  let pendingAmount = 0
  soldItems.forEach((item) => {
    if (
      item.paymentStatus !== PAYMENT_STATUS.PAID &&
      item.paymentType !== PAYMENT_TYPE.CARD &&
      !item.isScratched &&
      !item.refundAmountAdded
    ) {
      pendingAmount +=
        item.price + addSalesTax(item.created, item.price, item.includeSalesTax, chargeSalesTax)
    }
  })

  const bankedAmount = paidAmount - refundedAmount
  const forecastAmount = bankedAmount + pendingAmount

  return {
    paidAmount,
    soldAmount,
    refundedAmount,
    scratchedAmount,
    pendingAmount,
    forecastAmount,
    bankedAmount,
    paidItems,
  }
}
